import { useEffect, useState } from "react";
import useLocalStorage from "./useLocalStorage";

export type SupportedLocale = "de" | "en" | "all";

/**
 * Detects browser locale and returns appropriate locale code
 * German users get 'de', all others get 'en'
 */
function detectBrowserLocale(): SupportedLocale {
  try {
    const browserLang = navigator.language || navigator.languages?.[0];
    if (browserLang?.startsWith("de")) {
      return "de";
    }
    return "en";
  } catch (error) {
    console.log("Error detecting browser locale:", error);
    return "en";
  }
}

/**
 * Hook for managing locale state with automatic browser detection and localStorage persistence
 * Returns [currentLocale, setLocale] tuple
 */
export default function useLocale(): [
  SupportedLocale,
  (locale: SupportedLocale) => void,
] {
  const [storedLocale, setStoredLocale] = useLocalStorage<
    SupportedLocale | "auto"
  >("user-locale", "auto");
  const [currentLocale, setCurrentLocale] = useState<SupportedLocale>(() => {
    if (storedLocale === "auto") {
      return detectBrowserLocale();
    }
    return storedLocale;
  });

  useEffect(() => {
    if (storedLocale === "auto") {
      const detectedLocale = detectBrowserLocale();
      setCurrentLocale(detectedLocale);
    } else {
      setCurrentLocale(storedLocale);
    }
  }, [storedLocale]);

  const setLocale = (locale: SupportedLocale) => {
    if (locale === "de" || locale === "en" || locale === "all") {
      setStoredLocale(locale);
      setCurrentLocale(locale);
    } else {
      // If invalid locale, fall back to auto-detection
      setStoredLocale("auto");
      setCurrentLocale(detectBrowserLocale());
    }
  };

  return [currentLocale, setLocale];
}
